import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getAsset, type AssetResponse } from '../../api';
import { useAssetUrls, type AssetUrlLookup } from '../grid/useAssetUrls';
import type { Basket } from './useBasket';

/**
 * The assets the basket refers to, resolved independently of what is on screen.
 *
 * The grid fetches one status at a time, so `assets` on the page is only ever
 * the current tab's page of rows. The basket outlives that: it is filled on
 * Published, survives a switch to Staged, and survives a reload — at which
 * point nothing has been fetched at all. Reading basket thumbnails and names
 * out of the tab's list is what makes them collapse into "Asset unavailable"
 * the moment the user looks somewhere else.
 *
 * So the basket resolves its own. The page's list is preferred whenever it
 * has the asset, because that copy is the one the grid and the edit drawer are
 * writing to and it stays current across a save; only the ids missing from it
 * are fetched, one query each, keyed by id so two rows ordering the same key
 * visual share a single request and a single cache entry.
 */

export interface BasketAssets {
  /** Every basket asset that could be resolved, from either source. */
  assetById: Map<string, AssetResponse>;
  /** Signed URLs for the same, page-signed copies preferred. */
  urls: AssetUrlLookup;
}

export function useBasketAssets(
  basket: Basket,
  pageAssets: AssetResponse[],
  pageUrls: AssetUrlLookup,
): BasketAssets {
  const assetIds = useMemo(() => basket.lines.map((line) => line.assetId), [basket.lines]);
  return useResolvedAssets(assetIds, pageAssets, pageUrls);
}

const NO_ASSETS: AssetResponse[] = [];

/**
 * Any set of asset ids, resolved the way the basket resolves its own: from the
 * page's list where it has them, fetched one by one where it doesn't. A saved
 * brief's lines need the same — their assets are rarely on the page at all.
 *
 * Without `pageUrls` every URL comes from this hook's own signing call.
 */
export function useResolvedAssets(
  assetIds: string[],
  pageAssets: AssetResponse[] = NO_ASSETS,
  pageUrls?: AssetUrlLookup,
): BasketAssets {
  const pageAssetById = useMemo(
    () => new Map(pageAssets.map((asset) => [asset.id, asset])),
    [pageAssets],
  );

  // Deduped across rows: the same asset ordered for four markets is four lines
  // but one asset, and fetching it four times would be four signing calls for
  // one thumbnail.
  const missingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const assetId of assetIds) {
      if (!pageAssetById.has(assetId)) ids.add(assetId);
    }
    // Sorted so the value is order-independent — the basket reordering must not
    // look like a different set of assets to the hooks keyed on it below.
    return Array.from(ids).sort();
  }, [assetIds, pageAssetById]);

  // One query per id rather than a batch call, because there is no by-ids
  // endpoint and the ids are individually cached and individually reused: an
  // asset already fetched for the overlay is not fetched again here.
  const fetched = useQueries({
    queries: missingIds.map((assetId) => ({
      queryKey: ['asset-library', 'asset', assetId],
      queryFn: () => getAsset(assetId),
      // A 404 means deleted. Retrying can't change that, and the drawer already
      // has an honest empty state for a line it cannot resolve.
      retry: false,
      // These back a basket the user assembled minutes ago; re-fetching them on
      // every window focus would re-sign a stack of thumbnails each time.
      refetchOnWindowFocus: false,
    })),
  });

  const missingUrls = useAssetUrls(missingIds);

  const assetById = useMemo(() => {
    const map = new Map<string, AssetResponse>();
    for (const result of fetched) {
      if (result.data) map.set(result.data.id, result.data);
    }
    // Page copies last so they win: same reason the overlay prefers them, an
    // edit saved on this page is visible in the basket without a refetch.
    for (const [id, asset] of pageAssetById) map.set(id, asset);
    return map;
  }, [fetched, pageAssetById]);

  // One lookup for the basket components to read, so they don't have to know
  // which of the two queries a given URL came from. `refresh` invalidates by
  // key prefix, so either hook's copy re-signs both.
  const urls = useMemo<AssetUrlLookup>(
    () =>
      pageUrls
        ? {
            urlById: new Map([...missingUrls.urlById, ...pageUrls.urlById]),
            thumbnailById: new Map([...missingUrls.thumbnailById, ...pageUrls.thumbnailById]),
            missing: new Set([...missingUrls.missing, ...pageUrls.missing]),
            isLoading: pageUrls.isLoading || missingUrls.isLoading,
            refresh: pageUrls.refresh,
          }
        : missingUrls,
    [pageUrls, missingUrls],
  );

  return { assetById, urls };
}
